import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import api from '../config/api';
import { Tag as TagIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { BRAND } from '../config/brand';

const TagsLanding = () => {
  const [params] = useSearchParams();
  const pageParam = parseInt(params.get('page') || '1', 10);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  const sort = (params.get('sort') || 'popular').toLowerCase() === 'alpha' ? 'alpha' : 'popular';

  const { data, isLoading, error } = useQuery(
    ['tags:list', page, sort],
    async () => {
      const res = await api.get('/api/tags/list', {
        params: {
          page,
          limit: 100,
          sort
        }
      });
      return res.data;
    },
    { keepPreviousData: true, staleTime: 60 * 1000 }
  );

  const items = data?.items || [];
  const totalPages = Math.max(1, data?.totalPages || 1);

  const basePath = '/tags';
  const canonical = useMemo(() => {
    const params = [];
    if (sort !== 'popular') params.push(`sort=${sort}`);
    if (page > 1) params.push(`page=${page}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return `${BRAND.baseUrl}${basePath}${qs}`;
  }, [page, sort]);

  const prevUrl = (() => {
    const p = page - 1;
    if (p < 1) return null;
    const params = [];
    if (sort !== 'popular') params.push(`sort=${sort}`);
    if (p > 1) params.push(`page=${p}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return `${basePath}${qs}`;
  })();

  const nextUrl = (() => {
    if (page >= totalPages) return null;
    const p = page + 1;
    const params = [];
    if (sort !== 'popular') params.push(`sort=${sort}`);
    params.push(`page=${p}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return `${basePath}${qs}`;
  })();

  return (
    <>
      <SEO
        title="Теги"
        description="Тематическая навигация A–Я. Выбирайте темы и открывайте новые идеи."
        url={`${basePath}${canonical.replace(BRAND.baseUrl + basePath, '')}`}
        type="website"
        canonical={canonical}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Теги',
          description: 'Тематическая навигация A–Я. Выбирайте темы и открывайте новые идеи.',
          url: canonical,
          inLanguage: 'ru-RU',
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: items.slice(0, 100).map((t, idx) => {
              const tag = typeof t === 'string' ? t : (t?.tag || '');
              return {
                '@type': 'ListItem',
                position: idx + 1,
                url: `${BRAND.baseUrl}/tag/${encodeURIComponent(tag)}`,
                name: `#${tag}`
              };
            }).filter(Boolean)
          }
        }}
      />

      <Helmet>
        {prevUrl && <link rel="prev" href={`${BRAND.baseUrl}${prevUrl}`} />}
        {nextUrl && <link rel="next" href={`${BRAND.baseUrl}${nextUrl}`} />}
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 text-sm text-secondary-600">
            <Link to="/" className="hover:underline">Главная</Link>
            <span>/</span>
            <span className="text-secondary-900">Теги</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-secondary-900 flex items-center gap-2">
            <TagIcon className="w-6 h-6 text-primary-500" />
            <span>Теги</span>
          </h1>
          <p className="mt-2 text-secondary-700">
            Тематическая навигация A–Я. Сортировка: {sort === 'popular' ? 'популярные' : 'алфавит'}.
          </p>
        </div>

        {/* Status */}
        {isLoading && (
          <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-soft p-6 mb-6">
            <div className="text-secondary-700">Загрузка...</div>
          </div>
        )}
        {error && (
          <div className="rounded-2xl bg-white ring-1 ring-red-200 shadow-soft p-6 mb-6">
            <div className="text-red-700">Ошибка загрузки данных</div>
          </div>
        )}

        {/* Tags list */}
        {!isLoading && !error && (
          <>
            {items.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {items.map((item) => {
                  const tag = typeof item === 'string' ? item : (item?.tag || '');
                  const count = typeof item === 'object' ? item?.count : undefined;
                  if (!tag) return null;
                  return (
                    <Link
                      to={`/tag/${encodeURIComponent(tag)}`}
                      key={tag}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary-50 hover:bg-secondary-100 border border-secondary-200 text-secondary-800"
                    >
                      <TagIcon className="w-4 h-4 text-secondary-600" />
                      <span>#{tag}</span>
                      {typeof count === 'number' && <span className="text-secondary-500">({count})</span>}
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-soft p-6">
                <h2 className="text-lg font-semibold text-secondary-900">Популярные теги</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {['дизайн','интерьер','3d','архитектура','панорама','образование','технологии','природа','туризм','искусство','фотография','маркетинг','брендинг','UX','UI','идеи'].map((tag) => (
                    <Link
                      to={`/tag/${encodeURIComponent(tag)}`}
                      key={tag}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary-50 hover:bg-secondary-100 border border-secondary-200 text-secondary-800"
                    >
                      <TagIcon className="w-4 h-4 text-secondary-600" />
                      <span>#{tag}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Pagination */}
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link
                to={prevUrl || '#'}
                className={`inline-flex items-center px-3 py-2 rounded-lg border ${prevUrl ? 'text-secondary-800 hover:bg-secondary-50 border-secondary-200' : 'text-secondary-400 border-secondary-100 cursor-not-allowed'}`}
                aria-disabled={!prevUrl}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Предыдущая
              </Link>
              <span className="text-sm text-secondary-700">Стр. {page} из {totalPages}</span>
              <Link
                to={nextUrl || '#'}
                className={`inline-flex items-center px-3 py-2 rounded-lg border ${nextUrl ? 'text-secondary-800 hover:bg-secondary-50 border-secondary-200' : 'text-secondary-400 border-secondary-100 cursor-not-allowed'}`}
                aria-disabled={!nextUrl}
              >
                Следующая
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default TagsLanding;
